/**
 * react-leaflet-lite — wrapper React mínimo sobre Leaflet (vanilla).
 *
 * POR QUÉ EXISTE: `react-leaflet` se distribuye bajo la licencia Hippocratic-2.1
 * (con restricciones éticas de uso, no aprobada por OSI), lo que NO es apto para
 * un producto enterprise/comercial. El motor de render —Leaflet— es BSD-2-Clause,
 * totalmente permisivo. Este módulo reimplementa EXACTAMENTE la superficie de API
 * de react-leaflet que usa la app (MapContainer, TileLayer, Marker, Popup, Polyline,
 * useMap, useMapEvents) directamente sobre Leaflet, sin cambiar el render ni el
 * comportamiento. Drop-in: basta cambiar el `from 'react-leaflet'` por este archivo.
 *
 * Notas de fidelidad respecto a react-leaflet v4:
 *  - <MapContainer> crea el mapa UNA vez; `center`/`zoom`/opciones son iniciales
 *    (las actualizaciones se hacen imperativamente vía useMap, igual que en react-leaflet).
 *  - El `ref` de <MapContainer> resuelve a la instancia de Leaflet (L.Map).
 *  - <Popup> renderiza sus children React vía portal dentro del popup de Leaflet,
 *    manteniéndolos reactivos (idéntico al enfoque de react-leaflet).
 *  - Sobrevive a React.StrictMode (montaje doble en dev) gracias al cleanup que
 *    hace map.remove() y recrea limpio.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';

const MapContext = createContext(null);
const MarkerContext = createContext(null);

// ── Hooks ────────────────────────────────────────────────────────────────────
export function useMap() {
  return useContext(MapContext);
}

export function useMapEvents(handlers) {
  const map = useMap();
  useEffect(() => {
    if (!map) return undefined;
    map.on(handlers);
    return () => {
      map.off(handlers);
    };
    // `handlers` es un objeto nuevo en cada render → re-suscribe cada render,
    // mismo comportamiento (y costo) que react-leaflet.
  }, [map, handlers]);
  return map;
}

// ── MapContainer ──────────────────────────────────────────────────────────────
export const MapContainer = forwardRef(function MapContainer(
  { children, style, className, id, whenReady, placeholder, ...options },
  ref,
) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);

  // Reparte props: aria-*/data-*/role/title van al <div>; el resto son opciones de L.map.
  const divProps = {};
  const mapOptions = {};
  Object.entries(options).forEach(([key, value]) => {
    if (key.startsWith('aria-') || key.startsWith('data-') || key === 'role' || key === 'title') {
      divProps[key] = value;
    } else {
      mapOptions[key] = value;
    }
  });

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return undefined;
    const instance = L.map(containerRef.current, mapOptions);
    mapRef.current = instance;
    setMap(instance);
    if (typeof whenReady === 'function') instance.whenReady(whenReady);
    // Mover la cámara sobre un mapa oculto (display:none → contenedor 0×0)
    // revienta con "Invalid LatLng (NaN, NaN)" porque Leaflet proyecta sin
    // tamaño (p.ej. GpsMonitoreo mobile: toggle en "Lista" + tap en un
    // dispositivo). Se difiere la operación y se ejecuta cuando el contenedor
    // vuelve a medir >0.
    //
    // Cubre las cuatro entradas de cámara, no solo flyTo: `fitBounds` calcula el
    // zoom con getSize() en 0 y degenera a Infinity, y con un único punto llega a
    // LatLng(NaN, NaN) y lanza. Es el mismo bug con distinto nombre, y afecta a
    // los mapas montados en tabs, modales o detrás de un `hidden lg:block`.
    // `panTo` y `flyToBounds` caen acá solos: internamente resuelven contra
    // `this.setView`/`this.flyTo`, que ya son estas versiones diferidas.
    //
    // Se guarda solo la ÚLTIMA operación pendiente: dos encuadres seguidos sobre
    // un mapa oculto deben dejarlo donde lo dejaría el segundo, no reproducir la
    // secuencia entera cuando el contenedor aparece.
    const DEFERRABLE_VIEW_METHODS = ['flyTo', 'flyToBounds', 'fitBounds', 'setView'];
    const originalViewMethods = {};
    let pendingView = null;
    const hasSize = () => {
      const el = instance.getContainer();
      return !!el && el.offsetWidth > 0 && el.offsetHeight > 0;
    };
    DEFERRABLE_VIEW_METHODS.forEach((name) => {
      const original = instance[name].bind(instance);
      originalViewMethods[name] = original;
      instance[name] = (...args) => {
        if (hasSize()) return original(...args);
        pendingView = { name, args };
        return instance;
      };
    });
    // Recalcular el tamaño cuando el contenedor cambia de dimensiones (cambio de
    // tab, colapso del sidebar, resize de ventana, animación de entrada del panel).
    // Sin esto Leaflet conserva el tamaño inicial y deja franjas sin tiles —el
    // típico "media mapa en blanco" al abrir un mapa dentro de un panel.
    let resizeObs;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObs = new ResizeObserver(() => {
        const el = instance.getContainer();
        if (!el || el.offsetWidth === 0 || el.offsetHeight === 0) return;
        instance.invalidateSize({ animate: false });
        if (pendingView) {
          const { name, args } = pendingView;
          pendingView = null;
          originalViewMethods[name](...args);
        }
      });
      resizeObs.observe(containerRef.current);
    }
    return () => {
      if (resizeObs) resizeObs.disconnect();
      instance.remove();
      mapRef.current = null;
      setMap(null);
    };
    // Crear una sola vez; center/zoom/opciones son iniciales (igual que react-leaflet).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exponer la instancia de Leaflet vía ref (paridad con react-leaflet v4).
  useImperativeHandle(ref, () => map, [map]);

  return (
    <div ref={containerRef} style={style} className={className} id={id} {...divProps}>
      {map ? <MapContext.Provider value={map}>{children}</MapContext.Provider> : placeholder ?? null}
    </div>
  );
});

// ── TileLayer ──────────────────────────────────────────────────────────────────
export function TileLayer({ url, attribution, ...options }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!map) return undefined;
    // No pasar opciones `undefined` a L.tileLayer: pisarían los defaults de Leaflet
    // (p.ej. `subdomains: undefined` rompe _getSubdomain → "undefined.length").
    const tileOptions = { attribution };
    Object.entries(options).forEach(([k, v]) => { if (v !== undefined) tileOptions[k] = v; });
    const layer = L.tileLayer(url, tileOptions);
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      map.removeLayer(layer);
      layerRef.current = null;
    };
    // Recrea cuando cambia url/attribution (p.ej. claro↔oscuro). El resto de
    // opciones (subdomains, maxZoom) son estáticas en la práctica.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, url, attribution]);

  return null;
}

// ── Marker ─────────────────────────────────────────────────────────────────────
export function Marker({ position, icon, eventHandlers, zIndexOffset, children, ...options }) {
  const map = useMap();
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    if (!map) return undefined;
    // OJO: solo incluir `icon` si viene definido. Pasar `icon: undefined` pisa el
    // ícono por defecto de Leaflet (L.Icon.Default) y rompe _initIcon ("createIcon"
    // de undefined). Igual con zIndexOffset.
    const markerOptions = { ...options };
    if (icon !== undefined) markerOptions.icon = icon;
    if (zIndexOffset !== undefined) markerOptions.zIndexOffset = zIndexOffset;
    const instance = L.marker(position, markerOptions);
    instance.addTo(map);
    setMarker(instance);
    return () => {
      map.removeLayer(instance);
      setMarker(null);
    };
    // Crear una sola vez por marcador; las props se sincronizan abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  const posKey = Array.isArray(position) ? `${position[0]},${position[1]}` : JSON.stringify(position);
  useEffect(() => {
    if (marker && position) marker.setLatLng(position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker, posKey]);

  useEffect(() => {
    if (marker && icon) marker.setIcon(icon);
  }, [marker, icon]);

  useEffect(() => {
    if (marker && zIndexOffset != null) marker.setZIndexOffset(zIndexOffset);
  }, [marker, zIndexOffset]);

  useEffect(() => {
    if (!marker || !eventHandlers) return undefined;
    const entries = Object.entries(eventHandlers);
    entries.forEach(([evt, fn]) => marker.on(evt, fn));
    return () => entries.forEach(([evt, fn]) => marker.off(evt, fn));
  }, [marker, eventHandlers]);

  return marker && children ? (
    <MarkerContext.Provider value={marker}>{children}</MarkerContext.Provider>
  ) : null;
}

// ── Popup ──────────────────────────────────────────────────────────────────────
export function Popup({ children, ...options }) {
  const marker = useContext(MarkerContext);
  const containerRef = useRef(null);
  if (!containerRef.current) containerRef.current = document.createElement('div');

  useEffect(() => {
    if (!marker) return undefined;
    const popup = L.popup(options).setContent(containerRef.current);
    marker.bindPopup(popup);
    // Recalcula tamaño al abrir, una vez que el contenido React está montado.
    const handleOpen = () => popup.update();
    marker.on('popupopen', handleOpen);
    return () => {
      marker.off('popupopen', handleOpen);
      marker.unbindPopup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker]);

  return createPortal(children, containerRef.current);
}

// ── Polyline ───────────────────────────────────────────────────────────────────
export function Polyline({ positions, pathOptions, color, weight, opacity, back, children, ...options }) {
  const map = useMap();
  const layerRef = useRef(null);
  const [layer, setLayer] = useState(null);

  // Acepta tanto `pathOptions={{...}}` como props sueltas (color/weight/opacity).
  const style = { ...(pathOptions || {}) };
  if (color !== undefined) style.color = color;
  if (weight !== undefined) style.weight = weight;
  if (opacity !== undefined) style.opacity = opacity;

  useEffect(() => {
    if (!map) return undefined;
    const line = L.polyline(positions || [], { ...style, ...options });
    line.addTo(map);
    // `back`: capa de fondo — Leaflet pinta por orden de inserción, no de JSX, así
    // que una polyline montada tarde (p.ej. tras un fetch) taparía a las demás.
    if (back) line.bringToBack();
    layerRef.current = line;
    setLayer(line);
    return () => {
      map.removeLayer(line);
      layerRef.current = null;
      setLayer(null);
    };
    // Crear una sola vez; posiciones y estilo se sincronizan abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (layerRef.current) layerRef.current.setLatLngs(positions || []);
  }, [positions]);

  useEffect(() => {
    if (layerRef.current) layerRef.current.setStyle(style);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, weight, opacity, pathOptions]);

  // Permite anidar <Tooltip>/<Popup> dentro de <Polyline> (igual que react-leaflet).
  return layer && children ? <MarkerContext.Provider value={layer}>{children}</MarkerContext.Provider> : null;
}

// ── CircleMarker ─────────────────────────────────────────────────────────────
export function CircleMarker({ center, radius, pathOptions, color, fillColor, fillOpacity, weight, opacity, eventHandlers, children, ...options }) {
  const map = useMap();
  const [layer, setLayer] = useState(null);

  const style = { ...(pathOptions || {}) };
  if (radius !== undefined) style.radius = radius;
  if (color !== undefined) style.color = color;
  if (fillColor !== undefined) style.fillColor = fillColor;
  if (fillOpacity !== undefined) style.fillOpacity = fillOpacity;
  if (weight !== undefined) style.weight = weight;
  if (opacity !== undefined) style.opacity = opacity;

  useEffect(() => {
    if (!map) return undefined;
    const circle = L.circleMarker(center, { ...style, ...options });
    circle.addTo(map);
    setLayer(circle);
    return () => {
      map.removeLayer(circle);
      setLayer(null);
    };
    // Crear una sola vez; centro/estilo se sincronizan abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  const centerKey = Array.isArray(center) ? `${center[0]},${center[1]}` : JSON.stringify(center);
  useEffect(() => {
    if (layer && center) layer.setLatLng(center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, centerKey]);

  useEffect(() => {
    if (!layer) return;
    layer.setStyle(style);
    if (radius != null) layer.setRadius(radius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, radius, color, fillColor, fillOpacity, weight, opacity, pathOptions]);

  useEffect(() => {
    if (!layer || !eventHandlers) return undefined;
    const entries = Object.entries(eventHandlers);
    entries.forEach(([evt, fn]) => layer.on(evt, fn));
    return () => entries.forEach(([evt, fn]) => layer.off(evt, fn));
  }, [layer, eventHandlers]);

  return layer && children ? <MarkerContext.Provider value={layer}>{children}</MarkerContext.Provider> : null;
}

// ── Circle ──────────────────────────────────────────────────────────────────
// Equivalente a L.circle: círculo geográfico (radio en metros, no píxeles).
// Distinto de CircleMarker, que tiene radio en px. Útil para geocercas.
export function Circle({ center, radius, pathOptions, color, fillColor, fillOpacity, weight, opacity, dashArray, eventHandlers, children, ...options }) {
  const map = useMap();
  const [layer, setLayer] = useState(null);

  const style = { ...(pathOptions || {}) };
  if (color !== undefined) style.color = color;
  if (fillColor !== undefined) style.fillColor = fillColor;
  if (fillOpacity !== undefined) style.fillOpacity = fillOpacity;
  if (weight !== undefined) style.weight = weight;
  if (opacity !== undefined) style.opacity = opacity;
  if (dashArray !== undefined) style.dashArray = dashArray;

  useEffect(() => {
    if (!map) return undefined;
    const circle = L.circle(center, { ...style, radius, ...options });
    circle.addTo(map);
    setLayer(circle);
    return () => {
      map.removeLayer(circle);
      setLayer(null);
    };
    // Crear una sola vez; centro/radio/estilo se sincronizan abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  const centerKey = Array.isArray(center) ? `${center[0]},${center[1]}` : JSON.stringify(center);
  useEffect(() => {
    if (layer && center) layer.setLatLng(center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, centerKey]);

  useEffect(() => {
    if (!layer) return;
    layer.setStyle(style);
    if (radius != null) layer.setRadius(radius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, radius, color, fillColor, fillOpacity, weight, opacity, dashArray, pathOptions]);

  useEffect(() => {
    if (!layer || !eventHandlers) return undefined;
    const entries = Object.entries(eventHandlers);
    entries.forEach(([evt, fn]) => layer.on(evt, fn));
    return () => entries.forEach(([evt, fn]) => layer.off(evt, fn));
  }, [layer, eventHandlers]);

  return layer && children ? <MarkerContext.Provider value={layer}>{children}</MarkerContext.Provider> : null;
}

// ── Polygon ──────────────────────────────────────────────────────────────────
// Polígono geográfico cerrado (L.polygon). Acepta el mismo set de opciones de
// estilo que Polyline/Circle. Útil para geocercas poligonales.
export function Polygon({ positions, pathOptions, color, fillColor, fillOpacity, weight, opacity, dashArray, eventHandlers, children, ...options }) {
  const map = useMap();
  const [layer, setLayer] = useState(null);

  const style = { ...(pathOptions || {}) };
  if (color !== undefined) style.color = color;
  if (fillColor !== undefined) style.fillColor = fillColor;
  if (fillOpacity !== undefined) style.fillOpacity = fillOpacity;
  if (weight !== undefined) style.weight = weight;
  if (opacity !== undefined) style.opacity = opacity;
  if (dashArray !== undefined) style.dashArray = dashArray;

  useEffect(() => {
    if (!map) return undefined;
    const poly = L.polygon(positions || [], { ...style, ...options });
    poly.addTo(map);
    setLayer(poly);
    return () => {
      map.removeLayer(poly);
      setLayer(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (layer) layer.setLatLngs(positions || []);
  }, [layer, positions]);

  useEffect(() => {
    if (layer) layer.setStyle(style);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, color, fillColor, fillOpacity, weight, opacity, dashArray, pathOptions]);

  useEffect(() => {
    if (!layer || !eventHandlers) return undefined;
    const entries = Object.entries(eventHandlers);
    entries.forEach(([evt, fn]) => layer.on(evt, fn));
    return () => entries.forEach(([evt, fn]) => layer.off(evt, fn));
  }, [layer, eventHandlers]);

  return layer && children ? <MarkerContext.Provider value={layer}>{children}</MarkerContext.Provider> : null;
}

// ── Tooltip ──────────────────────────────────────────────────────────────────
// Se vincula a la capa padre (Marker/CircleMarker/Polyline) vía contexto, igual
// que react-leaflet. Soporta opciones permanent/direction/offset/sticky.
export function Tooltip({ children, ...options }) {
  const layer = useContext(MarkerContext);
  const containerRef = useRef(null);
  if (!containerRef.current) containerRef.current = document.createElement('div');

  useEffect(() => {
    if (!layer) return undefined;
    layer.bindTooltip(containerRef.current, options);
    return () => { try { layer.unbindTooltip(); } catch { /* capa ya removida */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer]);

  return createPortal(children, containerRef.current);
}
